/* jshint esversion: 6, -W097 */
/* globals module */
'use strict';

/**
 * Swiftype related services
 *
 * @module enervee_utils.swiftype
 */

SwiftypeService.$inject = ['$q', 'GetFetchService', 'UtilService', 'ProductApiConsumerService'];
function SwiftypeService($q, GetFetchService, UtilService, ProductApiConsumerService) {
    var _pub = {
        getProduct: getProduct,
        getProducts: getProducts,
        getResults: getResults,
        formatResults: formatResults,
        formatRecommendations: formatRecommendations,
        performAutocompleteSearch: performAutocompleteSearch,
        fetchSearchQuantity: fetchSearchQuantity
    };

    var DEFAULT_VARATIONS = [{
        'display_name': 'model_year_options',
        'swiftype_field': "model-year",
        'swiftype_group_field': 'model_year_group_json'
    }];

    var resultsUrl = ProductApiConsumerService.getFullUrl('api/1.0/swiftype/search/');
    var productUrl = ProductApiConsumerService.getFullUrl('api/1.0/swiftype/product/');
    var sponsoredOffersEnabled = UtilService.isFeatureEnabled('sponsored_product_offers');
    var variationSettings = UtilService.getSiteSetting('details_page_options_swiftype_fields', DEFAULT_VARATIONS);
    let fetchSearchQuantityCanceler = $q.defer();

    /**
     * Gets a product from swiftype by productId
     *
     * @param {String} productId
     * @param {String} categoryId
     * @param {String} categoryCode
     * @param {Boolean} [force] if true, re-fetch the data even if it's
     * already been fetched once
     * @returns {$q.promise}
     */
    function getProduct(productId, categoryId, categoryCode, force) {
        var url = productUrl + productId + '/?categoryId=' + categoryId;
        return GetFetchService.get(['swiftype-product', productId], url, {
            force: force,
            responseReturnFunction: function (response) {
                var records = response.data.records[categoryCode];
                if (records && records.length > 0) {
                    return formatProduct(records[0]);
                }
                return null;
            }
        });
    }

    /**
     * Passes the products (when the promise it resolved) based on the given category-code to
     * product-ids dictionary. If any of the products have been requested before, they are
     * loaded from memory (unless "force" is `true`).
     *
     * @param {Object} categoryCodesToProductIds A map of product id lists, where the key is
     * a category code, and the value is an array of product ids
     * @param {Boolean} [applyDefaultFilters] if false, default filters in category settings are ignored.
     * @param {Boolean} [force] if true, forces re-requesting of all the products from Swiftype.
     * @returns {Promise} resolves with a response with Swiftype
     */
    function getProducts(categoryCodesToProductIds, applyDefaultFilters, force) {
        var swiftypeOptions = {filters: {}, document_types: []};
        var allProducts = {};
        var storedProducts = GetFetchService.getRaw('swiftype-product') || {};
        var storedProductIds = Object.keys(storedProducts).map(function (id) {
            return parseInt(id);
        });

        angular.forEach(categoryCodesToProductIds, function (productIds, categoryCode) {
            if (!force) {
                var productsToRequest = UtilService.filterExcluded(productIds, storedProductIds);
                var productsAlreadyRequested = UtilService.filterIncluded(productIds, storedProductIds);
                productsAlreadyRequested.forEach(function (productId) {
                    allProducts[productId] = GetFetchService.getRaw(['swiftype-product', productId]);
                });
                if (productsToRequest.length > 0) {
                    swiftypeOptions.filters[categoryCode] = {external_id: productsToRequest};
                    swiftypeOptions.document_types.push(categoryCode);
                }
            } else {
                swiftypeOptions.filters[categoryCode] = {external_id: categoryCodesToProductIds[categoryCode]};
                swiftypeOptions.document_types.push(categoryCode);
            }
        });

        if (swiftypeOptions.document_types.length === 0) {
            return $q.resolve(allProducts);
        }

        if (applyDefaultFilters === undefined) {
            applyDefaultFilters = true;
        }
        swiftypeOptions.apply_default_filters = applyDefaultFilters;

        return getResults(swiftypeOptions).then(function (data) {
            if (data.record_count > 0) {
                var records = data.records;
                angular.forEach(categoryCodesToProductIds, function(_, categoryCode) {
                    if (!records[categoryCode]) {
                        return;
                    }
                    records[categoryCode].forEach(function(categoryProduct) {
                        allProducts[categoryProduct.id] = categoryProduct;
                        GetFetchService.update(['swiftype-product', categoryProduct.id], categoryProduct);
                    });
                });
            }

            return allProducts;
        });
    }

    /**
     * Returns results from the "search" endpoint of Swiftype
     *
     * @param {Object} options
     * @return {Promise} resolves to the data from the Swiftype response
     */
    function getResults(options) {
        return ProductApiConsumerService.post(resultsUrl, options).then(function success(response) {
            response.data.records = formatResults(response.data.records);
            response.data.resultsUrl = resultsUrl;
            return response.data;
        }, function error(response) {
            return $q.reject(response);
        });
    }

    /**
     * Queries Swiftype for a given search term
     * @param {String} searchTerm
     * @param {Object} options
     * @param {Promise} options.cancellerPromise
     * @param {Boolean} [options.eligibleForRebatesOnly=false]
     * @param {Boolean} [options.applyDefaultFilters=false]
     * @param {Array<String>} [options.documentTypes=[]] an array of category codes
     * @returns {*}
     */
    function performAutocompleteSearch(searchTerm, options) {
        options = options || {};
        if (options.applyDefaultFilters === undefined) {
            options.applyDefaultFilters = false;
        }

        if (options.eligibleForRebatesOnly === undefined) {
            options.eligibleForRebatesOnly = false;
        }

        if (options.applyFasttrackFilters === undefined) {
            options.applyFasttrackFilters = false;
        }

        var data = {
            query: searchTerm,
            apply_default_filters: !!options.applyDefaultFilters,
            apply_fasttrack_filters: !!options.applyFasttrackFilters,
            has_rebate: !!options.eligibleForRebatesOnly,
            headers: {
                Accept: "application/json",
            }
        };
        if (options.documentTypes !== undefined) {
            data.document_types = options.documentTypes;
        }

        var config = {};

        if (options.cancellerPromise) {
            config.timeout = options.cancellerPromise;
        }

        return ProductApiConsumerService.post('/api/1.0/swiftype/autocomplete/', data, config).then(function success(response) {
            return response.data.records;
        }, function error(response) {
            return $q.reject(response);
        });
    }

    /**
     * Takes the records data from the Swiftype search endpoint, formatting each product and
     * then returning the same structure.
     *
     * @param {Object} data an object of records data from Swiftype
     * @return {Object} formatted results data
     */
    function formatResults(data) {
        angular.forEach(data, function (records, category) {
            if (records) {
                data[category] = records.map(function (product) {
                    return formatProduct(product);
                });
            }
        });
        return data;
    }

    /**
     * Format recommended products objects to have all necessary parameters
     * @param {Object} recommendations an object of recommended products
     */
    function formatRecommendations(recommendations) {
        recommendations.forEach((recommendaton) => {
            recommendaton.product = formatProduct(recommendaton.product);
        });
    }

    /**
     * Takes an object from the Swiftype endpoint, formatting the product and
     * then returning the same structure.
     *
     * @param {Object} product an object of response data from Swiftype
     * @return {Object} formatted results data
     */
    function formatProduct(product) {
        // Copy the original product object
        var _product = Object.assign({}, product);

        // Sort the offers_json by total_price
        _product.offers_json = getSortedOffers(_product.offers_json);
        _product.top_retailer = getTopRetailerProps(_product.offers_json[0]);

        // if product has options show child with lowest price
        _product.lowest_priced_child = _pub.getLowestPricedChild(product, null);

        // Map the 3 top_specs in a cleaner format for object-flattening
        _product.top_specs_map = getTopSpecsMap(product.top_specs_json);

        for (var i=0; i<variationSettings.length; i++) {
            var variationSetting = variationSettings[i];
            
            var variationOptions = product[variationSetting.swiftype_group_field];
            if (variationOptions) {
                _product[variationSetting.display_name] = variationOptions;
            }
        }

        return _product;
    }

    /**
     * Returns a copy of the offers array, sorted by price and conditionally sorted by
     * sponsored offers.
     *
     * @param {Array<Object>} offers
     * @return {Array}
     */
    function getSortedOffers(offers) {
        if (!offers) {
            return [];
        }
        // Copy the original offers array
        var _offers = offers.slice(0);

        // If sponsored offers are not enabled, force all the "sponsored" properties to "false"
        if (!sponsoredOffersEnabled) {
            _offers = _offers.map(function (offer) {
                offer.sponsored = false;
                return offer;
            });
        }

        _offers.sort(function (a, b) {
            if (a.sponsored !== b.sponsored) {
                /**
                 * If the two items don't have the same "sponsored" status, then the one that is
                 * sponsored should be given priority. Since the "sponsored" property is a
                 * boolean, we're treating it like an integer by doing math with it (`true==1`
                 * and `false==0`). In this case, if "b.sponsored" is 1 and "a.sponsored" is 0,
                 * then "b" will be sorted closer to the beginning of the list.
                 */
                return b.sponsored - a.sponsored;
            }
            /**
             * If the sponsored statuses are the same (or the "sponsored_product_offers" feature
             * is not enabled, then we sort by price. If the value returned here is less than 0,
             * then "a" is sorted to a lower index (makes "a" come before "b"). Since the value
             * is `a.price - b.price`, the item with the lower price is sorted closer to the
             * beginning of the list.
             */
            return parseFloat(a.price) - parseFloat(b.price);
        });
        return _offers;
    }

    /**
     * Helper function to map the desired properties of the top retailer.
     * Returns empty object if no retailer is passed.
     *
     * @param {Object} [retailer]
     * @return {Object}
     */
    function getTopRetailerProps(retailer) {
        if (!retailer) {
            return {};
        }
        return {
            name: retailer.store_name,
            price: parseFloat(retailer.price),
            shipping: parseFloat(retailer.total_price) - parseFloat(retailer.price),
            sponsored: retailer.sponsored,
            url: retailer.link,
        };
    }

    /**
     * Helper function to return a map of the 3 top specs
     *
     * @param {Array<Object>} topSpecs
     * @return {Object}
     */
    function getTopSpecsMap(topSpecs) {
        if (!topSpecs) {
            return {};
        }
        return topSpecs.reduce(function (map, topSpec, index) {
            if (index > 2) {
                return map;
            }
            map[(index + 1).toString()] = {
                name: topSpec.name,
                value: topSpec.value,
            };
            return map;
        }, {});
    }

    /**
     * Gets all children available for checkout or just all children
     * if none availabe for checkout
     *
     * @param {Array} [variations]
     * @param {Object} [checkoutProductsById]
     * @returns {Array}
     */
    function getCheckoutVariations(variations, checkoutProductsById) {
        if (checkoutProductsById !== null) {
            var checkoutVariations = variations.filter( function (variation) {
                return checkoutProductsById.hasOwnProperty(variation.id);
            });
            if (checkoutVariations.length) {
                for (var i = 0; i < checkoutVariations.length; i++) {
                    var variation = checkoutVariations[i];
                    var rebateAmount = 0;
                    if (variation.rebate && variation.rebate !== null) {
                        rebateAmount = variation.rebate.amount;
                    }
                    variation.price = parseFloat(checkoutProductsById[variation.id].price) - parseFloat(rebateAmount);
                }
                return checkoutVariations;
            }
        }
        return variations;
    }

    /**
     * Gets the lowest priced child
     *
     * @returns {*}
     */
    _pub.getLowestPricedChild = function (product, checkoutProductsById) {

        var lowestPricedChild = null;

        var varyingFeatures = product.varying_features_json;

        if (varyingFeatures && varyingFeatures.length > 0) {

            for (var i = 0; i < varyingFeatures.length; i++) {
                var varyingFeature = varyingFeatures[i];
                var variations = getCheckoutVariations(varyingFeature.variations, checkoutProductsById);

                for (var j = 0; j < variations.length; j++) {

                    var childProduct = variations[j];

                    // The product itself shouldn't be considered as an option
                    if (childProduct.id === product.id) {
                        continue;
                    }

                    // Initialize lowestPricedChild to the first child
                    if (!lowestPricedChild) {
                        lowestPricedChild = childProduct;
                    } else if (parseFloat(childProduct.price) < parseFloat(lowestPricedChild.price)) {
                        lowestPricedChild = childProduct;
                    }

                }

            }
        }

        return lowestPricedChild;
    };

    /**
     * Queries Swiftype to get the number of products based on the filters
     *
     * @param {Object} options
     * @param {Promise} options.cancellerPromise
     * @param {Boolean} [options.eligibleForRebatesOnly=false]
     * @param {Boolean} [options.applyDefaultFilters=false]
     * @param {Boolean} [options.applyFasttrackFilters=false]
     * @param {Array<String>} [options.documentTypes=[]] an array of category codes
     * @returns {*}
     */
    function fetchSearchQuantity(options) {
        const config = cancelCurrentFetchSearchQuantityRequests();
        return ProductApiConsumerService.post('/api/1.0/swiftype/search-quantity/', options, config).then(function success(response) {
            return response.data;
        });
    }

    /**
     * Cancels the in progress requests and creates a new canceler promise for the new request
     *
     * @returns {{timeout: Promise}} canceler promise
     */
    function cancelCurrentFetchSearchQuantityRequests() {
        fetchSearchQuantityCanceler.resolve();
        fetchSearchQuantityCanceler = $q.defer();
        return {
            timeout: fetchSearchQuantityCanceler.promise
        };
    }

    return _pub;
}

module.exports = SwiftypeService;
